<template>
	<nta-page-layout :title="title" :subtitle="subtitle">
		<template #actions>
			<ConnectToAlternisSessionBtn v-if="domainStore.domain.id === AOKPlusDomainId" />
			<!-- DELETE WORKSPACES BTN -->
			<v-menu
				v-if="domainStore.hasAccessRights(UserRole.Editor) && workspacesInDataspace.length > 0"
				v-model="menuOpen"
				@click:outside="resetMenu"
				location="end"
			>
				<template v-slot:activator="{ props }">
					<v-btn variant="tonal" color="error" v-bind="props">{{
						symbolsStore.GUILabel("66afff98-79b6-43aa-a3e1-7439e602e499")
					}}</v-btn>
				</template>
				<v-card min-width="300" elevation="3">
					<v-list>
						<!-- DELETE ALL INACTIVE WORKSPACES-->
						<v-list-item
							@mousedown="startPressing(0)"
							@mouseup="stopPressing(0)"
							@mouseleave="stopPressing(0)"
							@click.stop
							:ripple="false"
							:style="getItemStyle(0)"
							class="menu-item"
						>
							<v-list-item-title>{{
								symbolsStore.GUILabel("7a9d44ac-72ca-4f08-8198-27c57d9fa14b", {
									label: symbolsStore.GUILabel("1f1e7900-59fa-4e7e-9926-d3bf8d0bbe4b"),
								})
							}}</v-list-item-title>
						</v-list-item>
						<!-- DELETE ALL WORKSPACES-->
						<v-list-item
							@mousedown="startPressing(1)"
							@mouseup="stopPressing(1)"
							@mouseleave="stopPressing(1)"
							@click.stop.prevent
							:ripple="false"
							:style="getItemStyle(1)"
							class="menu-item"
						>
							<v-list-item-title>{{
								symbolsStore.GUILabel("ee434246-619e-43e6-a189-6f06b6182be8", {
									label: symbolsStore.GUILabel("1f1e7900-59fa-4e7e-9926-d3bf8d0bbe4b"),
								})
							}}</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-card>
			</v-menu>
			<!-- CREATE NEW WORKSPACE BTN -->
			<v-btn
				v-show="domainStore.hasAccessRights(UserRole.Editor)"
				variant="tonal"
				color="success"
				min-width="6em"
				@click="newWorkspace"
			>
				{{ symbolsStore.GUILabel("5237160e-da5f-4562-9667-09fa59c952be") }}
			</v-btn>
		</template>
		<template #default>
			<component :is="displayResponsive ? 'v-col' : 'v-row'" class="mt-6">
				<transition-group name="list">
					<v-col v-for="workspaceId in workspacesInDataspace" :key="workspaceId">
						<WorkspaceCard
							:workspace-id="workspaceId"
							:stats="stats[workspaceId]"
							@click="clickWorkspace"
						/>
					</v-col>
				</transition-group>
			</component>
		</template>
	</nta-page-layout>
	<WorkspaceEditModal
		:show="editWorkspaceModal"
		mode="create"
		:loading="loading"
		@create="({ template, name, description }) => createWorkspace(template, name, description)"
		@done="() => (editWorkspaceModal = false)"
	/>
	<Members v-if="!displayResponsive && domainStore.isAdmin" />
	<v-snackbar v-model="snackbar.show" color="error" :timeout="3_000">
		<div>
			{{
				symbolsStore.GUILabel("227b6e9c-2054-4dd3-957c-55dcbb290e2c", {
					label:
						snackbar.number == 1
							? symbolsStore.GUILabel("bf45d746-d0ca-4e20-88ea-d646d99de966")
							: symbolsStore.GUILabel("1f1e7900-59fa-4e7e-9926-d3bf8d0bbe4b"),
					number: String(snackbar.number),
				})
			}}
		</div>
		<template v-slot:actions>
			<v-btn color="" variant="text" @click="snackbar.show = false">
				{{ symbolsStore.GUILabel("1ee57bfb-be22-4fc0-8ea1-1d749925fa78") }}
			</v-btn>
		</template>
	</v-snackbar>
</template>

<script setup lang="ts">
import router from "../../../router";
import WorkspaceCard from "../components/WorkspaceCard.vue";
import { computed, onMounted, onUnmounted, ref } from "vue";
import Members from "../../../components/Members.vue";
import WorkspaceEditModal from "../components/WorkspaceEditModal.vue";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";
import type { ChildProcessStats } from "@lr-notitia/notitia-operator/schemas";
import { useResponsiveLayout } from "@lr-notitia/notitia-ui";
import ConnectToAlternisSessionBtn from "../components/ConnectToAlternisSessionBtn.vue";

import { useDomainStore } from "../../../store/domain";
import { useInstanceWorkspacesStore } from "../../../store/i-workspace";
import { useInstanceDataspacesStore } from "../../../store/i-dataspace";
import { useSymbolsStore } from "../../../store/symbols";

export type SessionInfo = {
	"session-id": string;
	label: string;
	"created-at": number;
	"updated-at": number;
	state: "inactive" | "passive" | "active";
	"message-count": number | null;
	"message-languages": string[];
};

const domainStore = useDomainStore();
const instanceWorkspacesStore = useInstanceWorkspacesStore();
const instanceDataspacesStore = useInstanceDataspacesStore();
const symbolsStore = useSymbolsStore();

const domainId = router.currentRoute.value.params.domainId as string;
const dataspaceId = router.currentRoute.value.params.dataspaceId as string;

const AOKPlusDomainId = "c8b7bf72-f668-42af-89d8-4496d21bba18";

const { displayResponsive } = useResponsiveLayout();

const title = computed(
	() =>
		(displayResponsive
			? ""
			: symbolsStore.GUILabel("235c03b6-398a-40a2-8334-65e46ca26098", {
					label1: symbolsStore.GUILabel("1f1e7900-59fa-4e7e-9926-d3bf8d0bbe4b"),
					label2: symbolsStore.GUILabel("49e19b39-51b8-4ee9-948e-919d4e460600"),
			  })) +
		domainStore.domain.name +
		" -- " +
		(displayResponsive ? "" : symbolsStore.GUILabel("1fa57246-ae3e-4cbe-b38d-da224bedc183") + ": ") +
		instanceDataspacesStore.openedDataspace?.label,
);
const subtitle = computed(() =>
	symbolsStore.GUILabel("9b7de21d-6bff-4bc3-86c6-2bd655549345", {
		label: symbolsStore.GUILabel("1fa57246-ae3e-4cbe-b38d-da224bedc183"),
		count: String(instanceDataspacesStore.openedDataspace?.workspaces.length ?? 0),
		type:
			instanceDataspacesStore.openedDataspace?.workspaces.length == 1
				? symbolsStore.GUILabel("bf45d746-d0ca-4e20-88ea-d646d99de966")
				: symbolsStore.GUILabel("1f1e7900-59fa-4e7e-9926-d3bf8d0bbe4b"),
	}),
);
const editWorkspaceModal = ref(false);
const loading = ref(false);

interface MenuItemState {
	isPressed: boolean;
	progress: number;
	startTime: number;
	animationId: number | null;
}

const menuItems = ref<MenuItemState[]>([
	{ isPressed: false, progress: 0, startTime: 0, animationId: null },
	{ isPressed: false, progress: 0, startTime: 0, animationId: null },
]);

const menuOpen = ref(false);
const snackbar = ref<{
	show: boolean;
	number: number | null;
}>({
	show: false,
	number: null,
});

const pressDuration = 800;

function getItemStyle(index: number) {
	const progress = menuItems.value[index].progress;
	if (progress <= 0) return {};

	return {
		backgroundImage: `linear-gradient(to right, #e57373 ${progress}%, transparent ${progress}%)`,
	};
}

function startPressing(index: number) {
	const item = menuItems.value[index];

	if (item.animationId !== null) {
		cancelAnimationFrame(item.animationId);
	}

	item.isPressed = true;
	item.startTime = Date.now();
	updateProgress(index);
}

function updateProgress(index: number) {
	const item = menuItems.value[index];

	if (!item.isPressed) return;

	const elapsed = Date.now() - item.startTime;
	item.progress = Math.min((elapsed / pressDuration) * 100, 100);

	if (item.progress < 100) {
		item.animationId = requestAnimationFrame(() => updateProgress(index));
	} else {
		setTimeout(() => {
			handlePressCompleted(index);
		}, 200);
	}
}

async function handlePressCompleted(index: number) {
	const item = menuItems.value[index];
	item.isPressed = false;
	menuOpen.value = false;

	setTimeout(() => {
		item.progress = 0;
	}, 200);

	if (!domainStore.hasAccessRights(UserRole.Editor)) return;

	const deleteAll = index === 1;
	await deleteAllWorkspaces(deleteAll);
}

async function deleteAllWorkspaces(deleteAll: boolean = false) {
	if (!domainStore.hasAccessRights(UserRole.Editor)) return;

	let i = 0;
	for (const id of workspacesInDataspace.value) {
		if (!deleteAll && stats.value[id]) continue;
		i++;
		await instanceWorkspacesStore.removeWorkspace(id).catch((err) => console.error(err));
		let dataspace = instanceDataspacesStore.openedDataspace;
		if (!dataspace) return;
		await instanceDataspacesStore.updateDataspace({
			...dataspace,
			workspaces: dataspace.workspaces.filter((ws) => ws != id),
		});
		await instanceWorkspacesStore.deleteWorkspaceDescriptor(domainStore.domain.id, dataspace.id, id);
	}
	snackbar.value.number = i;
	snackbar.value.show = true;
}

function stopPressing(index: number) {
	const item = menuItems.value[index];
	item.isPressed = false;

	if (item.animationId !== null) {
		cancelAnimationFrame(item.animationId);
		item.animationId = null;
	}

	item.progress = 0;
}

function resetMenu() {
	menuOpen.value = false;
	menuItems.value.forEach((item) => {
		item.isPressed = false;
		item.progress = 0;
		if (item.animationId !== null) {
			cancelAnimationFrame(item.animationId);
			item.animationId = null;
		}
	});
}

const workspacesInDataspace = computed(() => {
	let dataspace = instanceDataspacesStore.openedDataspace;
	if (!dataspace) return [];
	return dataspace.workspaces.toReversed();
});
const stats = ref<Record<string, ChildProcessStats>>({});

function newWorkspace() {
	editWorkspaceModal.value = true;
}
function createWorkspace(templateId: string, name: string, description: string = "") {
	loading.value = true;
	if (description.length > 0) description = description + " by ";
	description =
		description +
		`${domainStore.account.firstname} ${domainStore.account.lastname} -  created: ${new Date(
			Date.now(),
		).toLocaleString()}`;
	instanceWorkspacesStore.addWorkspace(dataspaceId, templateId, name, description).then((d) => {
		let dataspace = instanceDataspacesStore.openedDataspace;
		if (!dataspace) return;
		dataspace.workspaces.push(d.id);
		instanceDataspacesStore.updateDataspace(dataspace);

		let descriptorNode: any = Object.values(d.graph.nodes).find(
			(n: any) => n.type === "workspace" && n.layer === "instance" && n.label && n.description,
		);
		if (descriptorNode) {
			instanceWorkspacesStore.setWorkspaceDescriptor(
				domainId,
				dataspaceId,
				d.id,
				descriptorNode.label,
				descriptorNode.description,
			);
		}

		loading.value = false;
		editWorkspaceModal.value = false;
	});
}

function clickWorkspace(id: string) {
	router.push("/" + domainId + "/template/" + dataspaceId + "/template/" + id);
}

//---------------------------------------------------------------------
const Input: any = {};

let statsInterval: any;
onMounted(() => {
	domainStore.loadDomain(domainId);
	instanceWorkspacesStore.loadWorkspaceDescriptors(domainId, dataspaceId);

	instanceWorkspacesStore.getStats().then((s) => {
		stats.value = s;
	});
	statsInterval = setInterval(() => {
		instanceWorkspacesStore.getStats().then((s) => {
			stats.value = s;
		});
	}, 30_000);

	window.addEventListener("keydown", function (e) {
		Input[e.key] = true;
		if (Input.Alt && Input.Dead) {
			newWorkspace();
		}
	});

	window.addEventListener("keyup", function (e) {
		Input[e.key] = false;
	});
});

onUnmounted(() => {
	clearInterval(statsInterval);
});
</script>

<style scoped>
.menu-item {
	position: relative;
	overflow: hidden;
	cursor: pointer;
}
</style>
